/**
 * Account + platform-config seam: the current user's device sessions, MFA
 * factors, notifications; and platform-wide config such as currencies, payment
 * gateways, broadcasts, and batch settings. Wraps the clientBatch mock consts
 * behind hooks so the mock→real flip is an env change (VITE_USE_MOCKS).
 *
 * Pure business-logic helpers (gatewaysForCurrency) and fixed config constants
 * (SESSION_CAP_*) are re-exported here so pages import them from the seam too,
 * never from mocks/ directly. The OTP/TOTP `verify*` functions branch the same
 * way every data resource does: mock-compare today, POST to the API on flip.
 */
import { useQuery } from '@tanstack/react-query';
import { USE_MOCKS, mockDelay, apiGet, apiGetList, apiPost, apiPatch, apiDelete } from './client';
import {
  currencies,
  paymentGatewaysDetailed,
  myActiveSessions,
  my2faFactors,
  my2faRecoveryCodes,
  myNotifications,
  broadcasts,
  platformBatchSettings,
  mockPrimary2faFactor,
  pendingModalBroadcasts,
  MOCK_VALID_OTP,
  MOCK_VALID_TOTP,
  type AppNotification,
  type BroadcastAudienceKind,
  type NotificationSeverity,
} from '../mocks/clientBatch';

/* ── Resource fetchers ───────────────────────────────────────────────────── */

/** The signed-in user's own account profile (GET/PATCH /me/profile). The Profile
 *  page loads this so saved fields (name, mobile) survive a refresh. */
export type MyProfile = {
  id: string;
  name: string;
  email: string | null;
  themePreference: string;
  preferredCurrency: string;
  contactPhone: string | null;
};
const MOCK_PROFILE: MyProfile = {
  id: 'usr_10', name: 'Imran Khan', email: 'imran@khan-bakery.com',
  themePreference: 'system', preferredCurrency: 'BDT', contactPhone: '+8801700000000',
};
export const getMyProfile = () =>
  USE_MOCKS ? mockDelay<MyProfile>(MOCK_PROFILE) : apiGet<MyProfile>('/me/profile');
export const updateMyProfile = (
  patch: { name?: string; contactPhone?: string | null; themePreference?: string; preferredCurrency?: string },
) =>
  USE_MOCKS
    ? mockDelay<MyProfile>({ ...MOCK_PROFILE, name: patch.name ?? MOCK_PROFILE.name, contactPhone: patch.contactPhone ?? null })
    : apiPatch<MyProfile>('/me/profile', patch);

export const listCurrencies = () =>
  USE_MOCKS ? mockDelay(currencies) : apiGetList<(typeof currencies)[number]>('/currencies');
export const listPaymentGateways = () =>
  USE_MOCKS ? mockDelay(paymentGatewaysDetailed) : apiGetList<(typeof paymentGatewaysDetailed)[number]>('/admin/gateways');
/** Revoke one of MY device sessions / sign out everywhere. */
export const revokeMySession = (sessionId: string) =>
  USE_MOCKS ? mockDelay({ ok: true }) : apiDelete(`/me/sessions/${sessionId}`);
export const revokeAllMySessions = () =>
  USE_MOCKS ? mockDelay({ ok: true }) : apiPost('/me/sessions/revoke-all', {});
/** Persist notification read-state. */
export const markNotificationRead = (id: string) =>
  USE_MOCKS ? mockDelay({ ok: true }) : apiPost(`/me/notifications/${id}/read`, {});
export const markAllNotificationsRead = () =>
  USE_MOCKS ? mockDelay({ ok: true, marked: 0 }) : apiPost('/me/notifications/read-all', {});

/** Persist a gateway's mode + credentials (server encrypts; reads return masked values only). */
export const updateGateway = (slug: string, patch: { mode?: 'live' | 'sandbox' | 'disabled'; config?: Record<string, string> }) =>
  USE_MOCKS ? mockDelay({ ok: true }) : apiPatch(`/admin/gateways/${slug}`, patch);
export const listMyActiveSessions = () =>
  USE_MOCKS ? mockDelay(myActiveSessions) : apiGetList<(typeof myActiveSessions)[number]>('/me/sessions');
export const listMy2faFactors = () =>
  USE_MOCKS ? mockDelay(my2faFactors) : apiGetList<(typeof my2faFactors)[number]>('/me/2fa/factors');
export const listMy2faRecoveryCodes = () =>
  USE_MOCKS ? mockDelay(my2faRecoveryCodes) : apiGetList<(typeof my2faRecoveryCodes)[number]>('/me/2fa/recovery-codes');
export const getMfaPrimaryFactor = () =>
  USE_MOCKS ? mockDelay(mockPrimary2faFactor) : apiGet<typeof mockPrimary2faFactor>('/me/2fa/primary');
export const listMyNotifications = () =>
  USE_MOCKS ? mockDelay(myNotifications) : apiGetList<(typeof myNotifications)[number]>('/me/notifications');
export const listBroadcasts = () =>
  USE_MOCKS ? mockDelay(broadcasts) : apiGetList<(typeof broadcasts)[number]>('/admin/broadcasts');
/** Compose a broadcast (delivered on the next scheduler tick when BROADCASTS_ENABLED). */
export type CreateBroadcastInput = {
  audienceKind: BroadcastAudienceKind;
  audienceTargetId?: string | null;
  audienceTargetLabel?: string | null;
  title: string;
  body: string;
  severity: NotificationSeverity;
  deliveryChannels: string[];
  scheduledAt?: string | null;
  pinnedUntil?: string | null;
};
export const createBroadcast = (input: CreateBroadcastInput) =>
  USE_MOCKS ? mockDelay({ id: `bc_${Date.now().toString(36)}`, ok: true }) : apiPost<{ id: string; ok: boolean; delivery?: string }>('/admin/broadcasts', input);
export const cancelBroadcast = (id: string) =>
  USE_MOCKS ? mockDelay({ ok: true }) : apiDelete(`/admin/broadcasts/${id}`);
// User-facing banner feed — every role can read this (the /admin one is platform-only).
export const listMyBroadcasts = () =>
  USE_MOCKS ? mockDelay(broadcasts) : apiGetList<(typeof broadcasts)[number]>('/me/broadcasts');
export const getPlatformBatchSettings = () =>
  USE_MOCKS ? mockDelay(platformBatchSettings) : apiGet<typeof platformBatchSettings>('/admin/settings');
export const getPendingModalBroadcasts = () =>
  USE_MOCKS ? mockDelay(pendingModalBroadcasts()) : apiGetList<AppNotification>('/me/notifications/modal');

/* ── Query hooks ─────────────────────────────────────────────────────────── */

export const useMyProfile = () => useQuery({ queryKey: ['me', 'profile'], queryFn: getMyProfile });
export const useCurrencies = () => useQuery({ queryKey: ['currencies'], queryFn: listCurrencies });
export const usePaymentGateways = () =>
  useQuery({ queryKey: ['admin', 'gateways'], queryFn: listPaymentGateways });
export const useMyActiveSessions = () =>
  useQuery({ queryKey: ['me', 'sessions'], queryFn: listMyActiveSessions });
export const useMy2faFactors = () => useQuery({ queryKey: ['me', '2fa', 'factors'], queryFn: listMy2faFactors });
export const useMy2faRecoveryCodes = () =>
  useQuery({ queryKey: ['me', '2fa', 'recovery'], queryFn: listMy2faRecoveryCodes });
export const useMfaPrimaryFactor = () =>
  useQuery({ queryKey: ['me', '2fa', 'primary'], queryFn: getMfaPrimaryFactor });
export const useMyNotifications = () =>
  useQuery({ queryKey: ['me', 'notifications'], queryFn: listMyNotifications });
export const useBroadcasts = () => useQuery({ queryKey: ['admin', 'broadcasts'], queryFn: listBroadcasts });
export const useMyBroadcasts = () => useQuery({ queryKey: ['me', 'broadcasts'], queryFn: listMyBroadcasts });
export const usePlatformBatchSettings = () =>
  useQuery({ queryKey: ['admin', 'settings'], queryFn: getPlatformBatchSettings });
export const usePendingModalBroadcasts = () =>
  useQuery({ queryKey: ['me', 'notifications', 'modal'], queryFn: getPendingModalBroadcasts });

/* ── Auth challenge verification ─────────────────────────────────────────────
 * Mock mode compares against the demo constant; on flip these POST to the API
 * and the constants drop out entirely (the backend owns the real check). */

export const verifyOtp = (code: string): Promise<boolean> =>
  USE_MOCKS
    ? mockDelay(code === MOCK_VALID_OTP)
    : apiPost<{ ok: boolean }>('/auth/verify-otp', { code }).then((r) => r.ok);

export const verifyTotp = (code: string): Promise<boolean> =>
  USE_MOCKS
    ? mockDelay(code === MOCK_VALID_TOTP)
    : apiPost<{ ok: boolean }>('/auth/mfa/verify', { code }).then((r) => r.ok);

/* ── Pure helpers / fixed config (re-exported so pages never touch mocks/) ─── */

export {
  gatewaysForCurrency,
  SESSION_CAP_DEFAULT,
  SESSION_CAP_MAX,
  SESSION_CAP_MIN,
} from '../mocks/clientBatch';
